import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Gauge, ShieldAlert, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { HitlDecide, Task } from "@/api/types";
import { tokenStorage } from "@/auth/tokenStorage";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { TaskStatusBadge } from "@/features/tasks/components/TaskStatusBadge";
import {
  isTaskActive,
  useCancelTask,
  useTask,
} from "@/features/tasks/hooks/useTasks";
import { ThreadTranscript } from "@/features/threads/components/ThreadTranscript";
import { formatDateTime, formatDuration } from "@/lib/date";
import { openSseStream } from "@/lib/sseClient";

export function TaskDetailPage() {
  const { taskId = "" } = useParams();
  const { t } = useTranslation("tasks");
  const qc = useQueryClient();
  const { data: task, isLoading, isError, error, refetch } = useTask(taskId);
  const cancelTask = useCancelTask();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  /**
   * Decide HITL inline (padrao decideAndDrain do inbox): o run vive no
   * worker, entao o SSE do decide e so um mini-stream -- drenamos e
   * repollamos; o polling do transcript pega a continuacao.
   */
  async function decideAndRefetch(hitlId: string, payload: HitlDecide) {
    const token = await tokenStorage.getToken();
    await new Promise<void>((resolve) => {
      openSseStream(
        {
          path: `/hitl-requests/${hitlId}/decide`,
          method: "POST",
          body: payload,
        },
        {
          token,
          onEvent: () => {},
          onClose: () => resolve(),
          onError: () => resolve(),
        },
      );
    });
    if (task) {
      void qc.invalidateQueries({
        queryKey: ["thread-messages", task.thread_id],
      });
      void qc.invalidateQueries({
        queryKey: ["thread-steps", task.thread_id],
      });
    }
    void qc.invalidateQueries({ queryKey: ["task", taskId] });
    void qc.invalidateQueries({ queryKey: ["tasks"] });
    void qc.invalidateQueries({ queryKey: ["hitl-requests"] });
  }

  async function handleCancel() {
    if (!task) return;
    try {
      await cancelTask.mutateAsync(task);
      toast.success(t("toasts.cancelRequested"));
      setConfirmCancelOpen(false);
      void refetch();
    } catch (err) {
      toastApiError(err);
    }
  }

  if (isLoading) return <LoadingState />;
  if (isError) {
    return <ErrorState error={error} onRetry={() => void refetch()} />;
  }
  if (!task) {
    return <EmptyState title={t("detail.notFound")} />;
  }

  const active = isTaskActive(task.status);
  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <PageHeader
        backTo="/tasks"
        title={task.title}
        description={<TaskMeta task={task} />}
        actions={
          active ? (
            <Button
              variant="outline"
              onClick={() => setConfirmCancelOpen(true)}
            >
              <XCircle className="h-4 w-4 text-danger-500" />
              {t("actions.cancel")}
            </Button>
          ) : undefined
        }
      />

      {task.status === "waiting_decision" ? (
        <div className="flex items-center gap-2 rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-700">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          {t("detail.waitingDecisionBanner")}
        </div>
      ) : null}
      {task.status === "error" && task.error_detail ? (
        <div className="flex items-center gap-2 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {task.error_detail}
        </div>
      ) : null}
      {task.status === "token_limit" ? (
        <div className="flex items-center gap-2 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          <Gauge className="h-4 w-4 shrink-0" />
          {t("detail.tokenLimitBanner", {
            used: task.tokens_used.toLocaleString("pt-BR"),
            max: (task.max_tokens ?? 0).toLocaleString("pt-BR"),
          })}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col">
        <ThreadTranscript
          threadId={task.thread_id}
          polling={active}
          onDecideHitl={decideAndRefetch}
        />
      </div>

      <AlertDialog
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cancelDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("cancelDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmCancelOpen(false)}
            >
              {t("actions.cancelDialog")}
            </Button>
            <Button
              variant="destructive"
              disabled={cancelTask.isPending}
              onClick={() => void handleCancel()}
            >
              {t("cancelDialog.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TaskMeta({ task }: { task: Task }) {
  const { t } = useTranslation("tasks");
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <TaskStatusBadge status={task.status} />
      <span>
        {task.agent_name ?? task.team_session_name ?? ""}
      </span>
      <span className="tabular-nums">
        {t("detail.tokens", {
          used: task.tokens_used.toLocaleString("pt-BR"),
        })}
        {task.max_tokens
          ? ` / ${task.max_tokens.toLocaleString("pt-BR")}`
          : ""}
      </span>
      <span>{formatDateTime(task.created_at)}</span>
      {task.started_at ? (
        <span>
          {t("detail.duration", {
            duration: formatDuration(task.started_at, task.finished_at),
          })}
        </span>
      ) : null}
    </span>
  );
}
